import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import AttachmentSidebarViewer from './AttachmentSidebarViewer';
import Notes from './Notes';
import UserAvatar from './UserAvatar';
import { cn } from '@/lib/utils';
import { CustomButton } from '@/components/ui/custom/CustomButton';
import {
	Dialog,
	DialogTitle,
	DialogContent,
	DialogFooter,
	DialogHeader,
	DialogDescription,
} from '@/components/ui/dialog';
import TeamConversationAvatar from './TeamConversationAvatar';
import { useIsMobile } from '@/hooks/use-mobile';
import { Button } from '@/components/ui/button';
import { X } from 'lucide-react';

// A titled part of the details panel, divided like the inbox's conversation details
const Section = ({ title, count, description, onboarding, children }) => (
	<section className="border-t border-border/60 px-4 py-3" data-onboarding={onboarding}>
		<h3 className="mb-2 flex items-center gap-1.5 text-sm font-medium">
			{title}
			{count !== undefined && (
				<span className="text-xs font-normal text-muted-foreground">{count}</span>
			)}
		</h3>
		{description && (
			<p className="mb-2 text-xs leading-5 text-muted-foreground">{description}</p>
		)}
		{children}
	</section>
);

const MemberRow = ({ member, isYou }) => (
	<div className="flex items-center gap-2.5 py-1">
		<UserAvatar
			user={member}
			showName={false}
			showNameBelow={false}
			avatarWidth="28px"
			avatarHeight="28px"
			avatarFontSize="11px"
			onlineIndicatorHeight="0.5rem"
			onlineIndicatorWidth="0.5rem"
		/>
		<span className="min-w-0 flex-1 truncate text-sm">
			{member.name}
			{isYou && <span className="text-muted-foreground"> (you)</span>}
		</span>
		{member.isOnline && (
			<span className="text-[11px] text-emerald-600 dark:text-emerald-400">Online</span>
		)}
	</div>
);

// Utility function to calculate online status
const calculateOnlineStatus = (currentUserToBeRendered, loggedInUserId) => {
	if (
		currentUserToBeRendered &&
		(currentUserToBeRendered._id === loggedInUserId ||
			currentUserToBeRendered.isOnline)
	) {
		return true;
	}
	return false;
};

const sortUsersByOnlineStatus = (users, loggedInUserId) => {
	const onlineUsers = [];
	const offlineUsers = [];

	users.forEach((user) => {
		const isOnline = calculateOnlineStatus(user, loggedInUserId);
		if (isOnline) {
			onlineUsers.push(user);
		} else {
			offlineUsers.push(user);
		}
	});

	return [...onlineUsers, ...offlineUsers];
};

const Sidebar = ({
	open,
	// 768-1279px, where the sidebar covers the thread: shows a close button
	onClose,
	closeButtonRef,
	handleIsAttachmentsModalOpen,
	isAttachmentsModalOpen,
}) => {
	const [showViewUsersDialog, setShowViewUsersDialog] = useState(false);

	const teams = useSelector((state) => state.teams);
	const auth = useSelector((state) => state.auth);
	const users = useSelector((state) => state.users);
	const { assignedTypeSelected } = useSelector((state) => state.webchat);
	const isMobile = useIsMobile();

	const directMessage = assignedTypeSelected === 'DIRECT_MESSAGES';

	// Phones show these details as the Info screen (TeamInfoPage), pushed from
	// the thread header, instead of a column beside the messages.
	if (isMobile) {
		return null;
	}

	if (!teams || !teams.teamConversations || !teams.teamConversations.length) {
		return null;
	}

	const { _activeTeamConversation } = teams;

	const activeTeamConversation = teams.teamConversations.find(
		(conv) => conv._id === _activeTeamConversation,
	);

	if (!activeTeamConversation) {
		return null;
	}

	const isRunner = activeTeamConversation?.isRunner;

	const userFilteredFromConversation = activeTeamConversation?._users.find(
		(u) => u !== auth.user._id,
	);

	const userFromDirectMessage = users.find(
		(u) => u._id === userFilteredFromConversation,
	);

	if (directMessage && !userFromDirectMessage) {
		return null;
	}

	const handleCloseShowUsersDialog = () => {
		setShowViewUsersDialog(false);
	};

	const handleOpenShowUsersDialog = () => {
		setShowViewUsersDialog(true);
	};

	const members = sortUsersByOnlineStatus(
		activeTeamConversation._users
			.map((u) => users.find((user) => user._id === u))
			.filter(Boolean),
		auth.user._id,
	);
	const isChannel = !!activeTeamConversation.channel;
	const memberCount = activeTeamConversation._users.length;

	return (
		<>
			{open ? (
				<div
					key={activeTeamConversation._id}
					className={cn(
						// a share of the chat's width, so the messages keep room in narrow panes
						isRunner ? 'w-full' : 'w-2/5 max-w-80 shrink-0',
						'flex h-full flex-col overflow-y-auto border-l border-border/60 bg-background',
					)}
				>
					{onClose && (
						// Pinned to the top corner while the details scroll.
						<div className="sticky top-0 z-10 flex h-0 shrink-0 justify-end">
							<Button
								ref={closeButtonRef}
								type="button"
								variant="ghost"
								size="icon"
								aria-label={directMessage ? 'Close message details' : 'Close group details'}
								onClick={onClose}
								className="mr-1 mt-1 size-11 shrink-0 rounded-full bg-background/80 text-muted-foreground backdrop-blur"
							>
								<X className="size-4" aria-hidden="true" />
							</Button>
						</div>
					)}
					<div className="flex flex-col items-center gap-2 px-4 pb-4 pt-6 text-center">
						<TeamConversationAvatar
							conversation={activeTeamConversation}
							directMessageUser={directMessage ? userFromDirectMessage : null}
							size={56}
						/>
						<div className="min-w-0 max-w-full">
							<p className="line-clamp-2 break-words text-[15px] font-medium leading-5">
								{directMessage
									? userFromDirectMessage.name
									: activeTeamConversation.title}
							</p>
							<p className="mt-0.5 truncate text-xs text-muted-foreground">
								{directMessage
									? userFromDirectMessage.username
									: `${isChannel ? 'Channel · ' : ''}${memberCount} ${
											memberCount === 1 ? 'member' : 'members'
										}`}
							</p>
						</div>
					</div>

					<Section
						title="Notes"
						onboarding="teams-notes"
						description={
							activeTeamConversation.notes.length === 0
								? 'Notes are shared with everyone here. Add one below, or pin a message to it.'
								: null
						}
					>
						<Notes
							activeTeamConversation={activeTeamConversation}
							key={activeTeamConversation._id}
						/>
					</Section>

					<Section title="Attachments">
						<AttachmentSidebarViewer
							isAttachmentsModalOpen={isAttachmentsModalOpen}
							handleIsAttachmentsModalOpen={handleIsAttachmentsModalOpen}
						/>
					</Section>

					{!directMessage && (
						<Section title="Members" count={memberCount}>
							{members.slice(0, 6).map((member) => (
								<MemberRow
									key={member._id}
									member={member}
									isYou={member._id === auth.user._id}
								/>
							))}
							{members.length > 6 && (
								<CustomButton
									variant="outline"
									size="sm"
									className="mt-2 w-full rounded-lg shadow-none"
									onClick={handleOpenShowUsersDialog}
								>
									View all {memberCount}
								</CustomButton>
							)}
							<Dialog
								open={showViewUsersDialog}
								onOpenChange={handleCloseShowUsersDialog}
							>
								<DialogContent>
									<DialogHeader>
										<DialogTitle className="pr-0.5">
											{`Members of ${activeTeamConversation.title}`}
										</DialogTitle>
										<DialogDescription>
											{memberCount} members
										</DialogDescription>
									</DialogHeader>
									<div className="max-h-72 overflow-y-auto">
										{members.map((member) => (
											<MemberRow
												key={member._id}
												member={member}
												isYou={member._id === auth.user._id}
											/>
										))}
									</div>
									<DialogFooter>
										<CustomButton
											variant="outline"
											onClick={handleCloseShowUsersDialog}
										>
											Close
										</CustomButton>
									</DialogFooter>
								</DialogContent>
							</Dialog>
						</Section>
					)}
				</div>
			) : null}
		</>
	);
};

export default Sidebar;
